import { useEffect, useMemo } from 'react'
import { create } from 'zustand'
import type { ImageDTO } from '@shared/types'
import { SESSION_WARN_BEFORE_MS } from '@shared/constants'
import { UNAUTHORIZED_EVENT } from '@/lib/api'
import { useAuthStore } from '@/stores/useAuthStore'
import { useMediaStore } from '@/stores/useMediaStore'

/* ── The data-loss safety net ─────────────────────────────────────────────────
   Nothing under a session survives it: sign out, hit the absolute expiry or go
   idle for two hours and the sites, images and every generated alt text are
   deleted. This module is the single source of "how long is left" and "how much
   would be lost", so every warning in the UI quotes the same numbers.
   ─────────────────────────────────────────────────────────────────────────── */

/** Coarse on purpose — the remaining time is displayed in minutes. */
const TICK_MS = 30_000

interface TickState {
  now: number
}

/**
 * One clock shared by every countdown. Per-component intervals would drift apart
 * and each re-render the shell on its own cadence.
 */
const useTickStore = create<TickState>(() => ({ now: Date.now() }))

let ticker: ReturnType<typeof setInterval> | null = null
let watchers = 0

/** Ref-counted, so no interval runs while only the login screen is mounted. */
function watchClock(): () => void {
  watchers += 1
  if (ticker === null) {
    // The store may have been left with a stale `now` by the previous watcher.
    useTickStore.setState({ now: Date.now() })
    ticker = setInterval(() => useTickStore.setState({ now: Date.now() }), TICK_MS)
  }
  return () => {
    watchers -= 1
    if (watchers > 0 || ticker === null) return
    clearInterval(ticker)
    ticker = null
  }
}

/** Milliseconds until `iso`, floored at zero. Null when there is no deadline. */
function remainingMs(iso: string | null, now: number): number | null {
  if (iso === null) return null
  const at = Date.parse(iso)
  if (Number.isNaN(at)) return null
  return Math.max(0, at - now)
}

/**
 * Mirrors the server's `needs-approval` and `needs-save` filters exactly:
 * generated or edited text still awaiting a decision, plus approved text not yet
 * written to WordPress. Either way the only copy is in this session's database
 * rows, so ending the session destroys it.
 */
function isUnsavedWork(image: ImageDTO): boolean {
  if (image.approved) return !image.saved
  return image.generatedAlt !== null || image.editedAlt !== null
}

/**
 * Alt text that exists only in this session. This is the number every warning
 * quotes, and the one that decides whether a warning appears at all.
 */
export function useUnsavedCount(): number {
  const items = useMediaStore((state) => state.items)
  const counts = useMediaStore((state) => state.counts)

  return useMemo(() => {
    const onPage = items.filter(isUnsavedWork).length
    // `items` is one page of one site; the server's filter counts cover the whole
    // open site, which is closer to what a sign-out actually destroys. An active
    // search narrows both, so treat this as a floor — for the guard it only has
    // to be non-zero whenever there is anything to lose.
    const siteWide = counts === null ? 0 : counts['needs-approval'] + counts['needs-save']
    return Math.max(onPage, siteWide)
  }, [items, counts])
}

/**
 * Where the "Save to WordPress" affordances send the user: the last site opened
 * in this tab, which is the one holding the unsaved rows. The media store keeps
 * `siteId` after the grid unmounts, so the link still works from Settings.
 */
export function useSavePath(): string {
  const siteId = useMediaStore((state) => state.siteId)
  return siteId === null ? '/sites' : `/sites/${siteId}`
}

/**
 * Drops the media state the ended session owned.
 *
 * The media store outlives a sign-out, so without this the next sign-in in the
 * same tab inherits the previous session's rows and every warning here quotes a
 * count for work that no longer exists — which is how a real warning stops being
 * believed. Only what this module reads is cleared; `open()` refills the rest.
 */
export function forgetSessionWork(): void {
  useMediaStore.setState({ siteId: null, items: [], counts: null })
}

// An expired or swept session is a sign-out the user did not ask for, and the
// rows it owned are already gone server-side.
window.addEventListener(UNAUTHORIZED_EVENT, forgetSessionWork)

export interface SessionGuard {
  /** Until the absolute ceiling, which no amount of activity can push back. */
  msUntilExpiry: number | null
  /** Until the idle sweep, which a heartbeat or a running job slides forward. */
  msUntilIdleExpiry: number | null
  /** Inside SESSION_WARN_BEFORE_MS of the absolute expiry. */
  expiringSoon: boolean
  unsavedCount: number
  savePath: string
}

export function useSessionGuard(): SessionGuard {
  const lifetime = useAuthStore((state) => state.lifetime)
  const now = useTickStore((state) => state.now)
  const unsavedCount = useUnsavedCount()
  const savePath = useSavePath()

  useEffect(watchClock, [])

  const msUntilExpiry = remainingMs(lifetime?.expiresAt ?? null, now)
  const msUntilIdleExpiry = remainingMs(lifetime?.idleExpiresAt ?? null, now)

  return {
    msUntilExpiry,
    msUntilIdleExpiry,
    // Deliberately the absolute expiry: the idle one keeps moving while the tab
    // is open, so warning on it would cry wolf at every user who is still here.
    expiringSoon: msUntilExpiry !== null && msUntilExpiry <= SESSION_WARN_BEFORE_MS,
    unsavedCount,
    savePath
  }
}
